import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { loadAllAndAssert, loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { normalizeSubject } from '../subjects.js';
import { linkExamToClassroom } from '../utils/classroom.js';
import { syncTeacherClassFromExam } from '../utils/teacher-classes.js';

export const updateExamDataMutation = registerMutation({
  operationName: 'updateExamDataMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'newTitle', type: () => String },
    { name: 'newSubject', type: () => String },
    { name: 'newTopic', type: () => String },
    { name: 'newClass', type: () => String },
  ],
  resolve: (examId: string, newTitle: string, newSubject: string, newTopic: string, newClass: string) => async () => {
    if (!newTitle || newTitle.trim().length === 0) {
      throw new Error('Der Titel darf nicht leer sein.');
    }
    // Also authorizes the classroom link below: it resolves classrooms through the exam owner's teacher profile.
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });

    const examCollection = await getCollection(Exam);
    const subject = normalizeSubject(newSubject);
    const klasse = newClass.trim();

    // Run the same classroom lookup/link the chat tools use (see linkExamToClassroom), so a manual
    // sidebar edit ends up tagged with the same canonical class name as the managed ClassRoom instead
    // of an arbitrary-cased/whitespaced string that silently falls out of the class exam overview.
    const resolvedClass = klasse ? (await linkExamToClassroom(examId, klasse, subject)).resolvedClass : klasse;

    const result = await examCollection.updateOne(
      { _id: examId },
      { $set: { title: newTitle, subject, topic: newTopic, class: resolvedClass } },
    );

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    await syncTeacherClassFromExam(examId, resolvedClass);

    return true;
  },
});

export const updateExamGroupMutation = registerMutation({
  operationName: 'updateExamGroupMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'newGroup', type: () => String },
  ],

  resolve: (examId: string, newGroup: string) => async () => {
    if (!newGroup || newGroup.trim().length === 0) {
      throw new Error('Die Gruppe darf nicht leer sein.');
    }
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });

    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne({ _id: examId }, { $set: { group: newGroup } });

    if (result.matchedCount === 0) {
      throw new Error(`Klausur mit ID ${examId} nicht gefunden.`);
    }

    return true;
  },
});

/** The only arrays this op may write (file-intake.ts persistRoleArtifact) — never owner/state fields. */
const UPDATABLE_EXAM_ARRAYS = ['masterSolutionIds', 'gradingSchemeIds', 'blankExamIds'];

export const updateExamArrayMutation = registerMutation({
  operationName: 'updateExamArrayMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'arrayName', type: () => String },
    { name: 'arrayValues', type: () => [String] },
  ],

  resolve: (examId: string, arrayName: string, arrayValues: string[]) => async () => {
    if (!arrayValues || arrayValues.length === 0) {
      throw new Error(`Das Array "${arrayName}" darf nicht leer sein.`);
    }
    if (!UPDATABLE_EXAM_ARRAYS.includes(arrayName)) {
      throw new AccessDeniedError('Access denied: field not updatable', { reason: 'array field not whitelisted' });
    }
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    // The ids are attached to the exam (and later processed/shared with it) — only files the caller may read.
    await loadAllAndAssert(AdornisFile, arrayValues, 'read', { _id: 1 });

    const examCollection = await getCollection(Exam);

    if (arrayValues.length === 0) {
      return true; // Keine neuen Werte, aber Update ist technisch erfolgreich
    }
    const updatedArrayValues = [...arrayValues];
    await examCollection.updateOne({ _id: examId }, { $set: { [arrayName]: updatedArrayValues } });

    return true;
  },
});

export const updateExamGradingSchemeMutation = registerMutation({
  operationName: 'updateExamGradingSchemeMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'gradingSchemeIds', type: () => [String] },
  ],

  resolve: (examId: string, gradingSchemeIds: string[]) => async () => {
    if (!gradingSchemeIds || gradingSchemeIds.length === 0) {
      throw new Error('Die Bewertungsschema darf nicht leer sein.');
    }
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    // The ids are attached to the exam (and later processed/shared with it) — only files the caller may read.
    await loadAllAndAssert(AdornisFile, gradingSchemeIds, 'read', { _id: 1 });

    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    const existingGradingSchemeIds = exam?.gradingSchemeIds || [];
    const newIdsToAdd = gradingSchemeIds.filter(id => !existingGradingSchemeIds.includes(id));
    if (newIdsToAdd.length === 0) {
      return true; // Keine neuen IDs, aber Update ist technisch erfolgreich
    }
    const updatedGradingSchemeIds = [...existingGradingSchemeIds, ...newIdsToAdd];
    await examCollection.updateOne({ _id: examId }, { $set: { gradingSchemeIds: updatedGradingSchemeIds } });

    return true;
  },
});

export const updateExamBlankExamMutation = registerMutation({
  operationName: 'updateExamBlankExamMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'blankExamIds', type: () => [String] },
  ],

  resolve: (examId: string, blankExamIds: string[]) => async () => {
    if (!blankExamIds || blankExamIds.length === 0) {
      throw new Error('Die leere Klausur darf nicht leer sein.');
    }
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    // The ids are attached to the exam (and later processed/shared with it) — only files the caller may read.
    await loadAllAndAssert(AdornisFile, blankExamIds, 'read', { _id: 1 });

    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    const existingBlankExamIds = exam?.blankExamIds || [];
    const newIdsToAdd = blankExamIds.filter(id => !existingBlankExamIds.includes(id));
    if (newIdsToAdd.length === 0) {
      return true; // Keine neuen IDs, aber Update ist technisch erfolgreich
    }
    const updatedBlankExamIds = [...existingBlankExamIds, ...newIdsToAdd];
    await examCollection.updateOne({ _id: examId }, { $set: { blankExamIds: updatedBlankExamIds } });

    return true;
  },
});
